'use client'

import {
  type ApplySurveyVariant,
  ApplySurveyVariantValidator,
} from '@app/p/apply-survey/[token]/conditions/[step]/ApplySurveyVariantValidator'
import getComorbidityMedicationsVariantAction from '@app/p/apply-survey/[token]/conditions/[step]/getComorbidityMedicationsVariantAction'
import { ConditionAttributes } from '@components/hooks/PatientTrialApplySurveyProvider/SurveyQuestionsConditions'
import { isMobileScreen } from '@components/hooks/useIsMobileScreen'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons/faSpinnerThird'
import { faStethoscope } from '@fortawesome/pro-regular-svg-icons/faStethoscope'
import { algoliaReadClient } from '@lib/database/algoliaPublicClient'
import { getAlgoliaSearchIndex } from '@lib/database/getAlgoliaSearchIndex'
import { NO_CONDITIONS_VALUE } from '@lib/questionnaire/trialApplication/generatePrescreeningQuestionConfig'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { isNotEmptyOrSkippedAnswer } from '@modules/patientApplyProfile/helpers/PatientApplyProfileData'
import type { ConceptsByFrequency } from '@modules/trials/helpers/extractEntitiesFromEligibilityCriteria/types'
import uniqBy from 'lodash/uniqBy'
import { useSearchParams } from 'next/navigation'
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { InstantSearch } from 'react-instantsearch'
import ConditionSearch from '../ConditionSearch'
import ApplySelectOption from './ApplySelectOption'
import { getCommonComorbidities } from './getCommonComorbidities'
import { PopularItems } from './PopularItems.client'
import { SelectedItems } from './SelectedItems.client'
import type { ConditionCollectElementProps } from './types'

const MAX_DISQUALIFYING_CONDITIONS = 5
const TRACKING_TYPE = 'comorbidity'

export default function CollectComorbidities({
  disqualifyingConditions,
  nctId,
  patientProfileData,
  therapeuticAreas,
  trialApplicationIdToken,
}: ConditionCollectElementProps) {
  const searchParams = useSearchParams()
  const maybeVariant = searchParams?.get('variant')

  const parseResult = ApplySurveyVariantValidator.safeParse(maybeVariant)
  const queryParamVariant = parseResult.success ? parseResult.data : undefined

  const [variant, setVariant] = useState<ApplySurveyVariant | undefined>(
    queryParamVariant,
  )

  useEffect(() => {
    if (variant) return

    getComorbidityMedicationsVariantAction({
      applyIdToken: trialApplicationIdToken,
      patientIsOnMobile: isMobileScreen(),
    }).then((v) => {
      setVariant(v)
      trackEvent(TrackingEvents.APPLY_FLOW_UI_VARIANT_VIEWED, {
        trialApplicationIdToken,
        type: TRACKING_TYPE,
        variant: v,
      })
    })

    return () => {
      trackEvent(TrackingEvents.APPLY_FLOW_UI_VARIANT_COMPLETED, {
        trialApplicationIdToken,
        type: TRACKING_TYPE,
        val: selectedConditions.join(', '),
        variant,
      })
    }
  }, [])

  const conditionsApplyingTo = patientProfileData.conditions
    .filter((condition) => condition !== NO_CONDITIONS_VALUE)
    .map((condition) => condition.toLowerCase())

  const commonComorbidityNames = getCommonComorbidities({
    condition: conditionsApplyingTo[0],
    nctId,
    therapeuticAreas,
  })

  const uniqueComorbidities = uniqBy(
    disqualifyingConditions
      .slice(0, MAX_DISQUALIFYING_CONDITIONS)
      .concat(commonComorbidityNames),
    (condition) => condition.toLowerCase(),
  )

  const comorbiditiesToFilterOut = useMemo(
    () =>
      [
        ...patientProfileData.unselectedComorbidities,
        ...patientProfileData.conditionSpecificComorbidities,
        ...patientProfileData.unselectedConditionSpecificComorbidities,
        ...conditionsApplyingTo,
      ].map((c) => c.toLowerCase()),
    [conditionsApplyingTo],
  )

  const comorbiditiesToDisplay = useMemo(
    () =>
      uniqueComorbidities
        // Filter out options already selected/shown on previous steps
        .filter(
          (name) => !comorbiditiesToFilterOut.includes(name.toLowerCase()),
        )
        .map((name, index) => ({
          frequency: 1,
          id: index,
          name,
        })),
    [comorbiditiesToFilterOut],
  )

  const [selectedConditions, setSelectedConditions] = useState(
    patientProfileData.comorbidities.filter(isNotEmptyOrSkippedAnswer),
  )
  const [comorbidities, setComorbidities] = useState<ConceptsByFrequency>(
    comorbiditiesToDisplay,
  )

  const handleSelectedConditions = (condition: string) => {
    if (!comorbidities.some((c) => c.name === condition)) {
      setComorbidities((prev) => [
        {
          frequency: 1,
          id: Date.now(),
          name: condition,
        },
        ...prev,
      ])
    }
    setSelectedConditions((selectedConditions) =>
      selectedConditions.includes(condition)
        ? selectedConditions.filter((c) => c !== condition)
        : [...selectedConditions, condition],
    )
  }

  switch (variant) {
    case 'desktop-1':
    case 'mobile-1':
      return (
        <CollectComorbiditiesInputVariant1
          comorbiditiesToDisplay={comorbidities}
          handleSelectedConditions={handleSelectedConditions}
          selectedConditions={selectedConditions}
        />
      )
    case 'desktop-2':
      return (
        <CollectComorbiditiesInputVariant2
          comorbiditiesToDisplay={comorbidities}
          handleSelectedConditions={handleSelectedConditions}
          selectedConditions={selectedConditions}
        />
      )
    case 'mobile-2':
      return (
        <CollectComorbiditiesInputVariant3
          comorbiditiesToDisplay={comorbidities}
          handleSelectedConditions={handleSelectedConditions}
          selectedConditions={selectedConditions}
        />
      )
    case 'control-desktop':
    case 'control-mobile':
    case 'control':
      return (
        <CollectComorbiditiesInput
          comorbiditiesToDisplay={comorbidities}
          handleSelectedConditions={handleSelectedConditions}
          selectedConditions={selectedConditions}
          setSelectedConditions={setSelectedConditions}
        />
      )
    default:
      return (
        <div className='flex size-full items-center justify-center'>
          <Icon
            className='animate-spin text-primary600'
            icon={faSpinnerThird}
            size='2x'
          />
        </div>
      )
  }
}

function CollectComorbiditiesInput({
  comorbiditiesToDisplay,
  handleSelectedConditions,
  selectedConditions,
  setSelectedConditions,
}: {
  comorbiditiesToDisplay: ConceptsByFrequency
  handleSelectedConditions: (condition: string) => void
  selectedConditions: string[]
  setSelectedConditions: Dispatch<SetStateAction<string[]>>
}) {
  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element='p'
        styleName='h3'
        value='Have you been diagnosed with any other conditions?'
      />
      <Text
        className='text-neutral900'
        element='p'
        styleName='p-small'
        value='Add all of your diagnosed conditions to get the most accurate study matches.'
      />

      <InstantSearch
        indexName={getAlgoliaSearchIndex('trials-by-site-locations')}
        searchClient={algoliaReadClient}
      >
        <ConditionSearch
          attribute='conditions'
          autoFocus
          highlightColor='text-primary600'
          inputClassName='rounded-lg text-sm bg-white border-2 border-neutral100 h-12 focus:border-blue500 focus:border-2'
          onSelect={handleSelectedConditions}
          placeholder='Condition'
        />
      </InstantSearch>

      <SelectedItems
        icon={faStethoscope}
        onChange={setSelectedConditions}
        title='Your conditions'
        trackingType={TRACKING_TYPE}
        value={selectedConditions}
      />

      {selectedConditions.filter(Boolean).map((condition) => (
        <input
          key={condition}
          name={ConditionAttributes.comorbidities}
          type='hidden'
          value={condition}
        />
      ))}

      {comorbiditiesToDisplay
        .filter((comorbidity) => !selectedConditions.includes(comorbidity.name))
        .map((unselectedComorbidity) => (
          <input
            key={unselectedComorbidity.name}
            name={ConditionAttributes.unselectedComorbidities}
            type='hidden'
            value={unselectedComorbidity.name}
          />
        ))}

      <PopularItems
        addNoneOption
        itemsByFrequency={comorbiditiesToDisplay}
        onChange={setSelectedConditions}
        title='Common Related Conditions'
        trackingType={TRACKING_TYPE}
        value={selectedConditions}
      />
    </div>
  )
}

function CollectComorbiditiesInputVariant1({
  comorbiditiesToDisplay,
  handleSelectedConditions,
  selectedConditions,
}: {
  comorbiditiesToDisplay: ConceptsByFrequency
  handleSelectedConditions: (condition: string) => void
  selectedConditions: string[]
}) {
  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element='p'
        styleName='h3'
        value='Have you been diagnosed with any other conditions?'
      />
      <Text
        className='text-neutral900'
        element='p'
        styleName='p-small'
        value='Add all of your diagnosed conditions to get the most accurate study matches.'
      />

      <InstantSearch
        indexName={getAlgoliaSearchIndex('trials-by-site-locations')}
        searchClient={algoliaReadClient}
      >
        <ConditionSearch
          attribute='conditions'
          autoFocus
          highlightColor='text-primary600'
          inputClassName='rounded-lg text-sm bg-white border-2 border-neutral100 h-12 focus:border-blue500 focus:border-2'
          onSelect={handleSelectedConditions}
          placeholder='Condition'
        />
      </InstantSearch>

      <div className='mt-4 space-y-4'>
        {comorbiditiesToDisplay.map((comorbidity) => {
          const selected = selectedConditions.includes(comorbidity.name)

          return (
            <ApplySelectOption
              key={comorbidity.name}
              label={comorbidity.name}
              onClick={handleSelectedConditions}
              selected={selected}
              value={comorbidity.name}
            />
          )
        })}
      </div>

      {selectedConditions.filter(Boolean).map((condition) => (
        <input
          key={condition}
          name='comorbidities'
          type='hidden'
          value={condition}
        />
      ))}

      {comorbiditiesToDisplay
        .filter((comorbidity) => !selectedConditions.includes(comorbidity.name))
        .map((unselectedComorbidity) => (
          <input
            key={unselectedComorbidity.name}
            name='unselectedComorbidities'
            type='hidden'
            value={unselectedComorbidity.name}
          />
        ))}
    </div>
  )
}

function CollectComorbiditiesInputVariant2({
  comorbiditiesToDisplay,
  handleSelectedConditions,
  selectedConditions,
}: {
  comorbiditiesToDisplay: ConceptsByFrequency
  handleSelectedConditions: (condition: string) => void
  selectedConditions: string[]
}) {
  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element='p'
        styleName='h3'
        value='Have you been diagnosed with any other conditions?'
      />
      <Text
        className='text-neutral900'
        element='p'
        styleName='p-small'
        value='Add all of your diagnosed conditions to get the most accurate study matches.'
      />

      <InstantSearch
        indexName={getAlgoliaSearchIndex('trials-by-site-locations')}
        searchClient={algoliaReadClient}
      >
        <ConditionSearch
          attribute='conditions'
          autoFocus
          highlightColor='text-primary600'
          inputClassName='rounded-lg text-sm bg-white border-2 border-neutral100 h-12 focus:border-blue500 focus:border-2'
          onSelect={handleSelectedConditions}
          placeholder='Condition'
        />
      </InstantSearch>

      <div className='mt-4 grid grid-cols-2 gap-4'>
        {comorbiditiesToDisplay.map((comorbidity) => {
          const selected = selectedConditions.includes(comorbidity.name)

          return (
            <ApplySelectOption
              className='h-[72px]'
              key={comorbidity.name}
              label={comorbidity.name}
              onClick={handleSelectedConditions}
              selected={selected}
              value={comorbidity.name}
            />
          )
        })}
      </div>

      {selectedConditions.filter(Boolean).map((condition) => (
        <input
          key={condition}
          name='comorbidities'
          type='hidden'
          value={condition}
        />
      ))}

      {comorbiditiesToDisplay
        .filter((comorbidity) => !selectedConditions.includes(comorbidity.name))
        .map((unselectedComorbidity) => (
          <input
            key={unselectedComorbidity.name}
            name='unselectedComorbidities'
            type='hidden'
            value={unselectedComorbidity.name}
          />
        ))}
    </div>
  )
}

function CollectComorbiditiesInputVariant3({
  comorbiditiesToDisplay,
  handleSelectedConditions,
  selectedConditions,
}: {
  comorbiditiesToDisplay: ConceptsByFrequency
  handleSelectedConditions: (condition: string) => void
  selectedConditions: string[]
}) {
  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element='p'
        styleName='h3'
        value='Have you been diagnosed with any other conditions?'
      />
      <Text
        className='text-neutral900'
        element='p'
        styleName='p-small'
        value='Add all of your diagnosed conditions to get the most accurate study matches.'
      />

      <div className='mt-4 space-y-4'>
        {comorbiditiesToDisplay.map((comorbidity) => {
          const selected = selectedConditions.includes(comorbidity.name)

          return (
            <ApplySelectOption
              key={comorbidity.name}
              label={comorbidity.name}
              onClick={handleSelectedConditions}
              selected={selected}
              value={comorbidity.name}
            />
          )
        })}
      </div>

      <InstantSearch
        indexName={getAlgoliaSearchIndex('trials-by-site-locations')}
        searchClient={algoliaReadClient}
      >
        <ConditionSearch
          attribute='conditions'
          autoFocus
          highlightColor='text-primary600'
          inputClassName='rounded-lg text-sm bg-white border-2 border-neutral100 h-12 focus:border-blue500 focus:border-2'
          menuPlacement='top'
          onSelect={handleSelectedConditions}
          placeholder='Condition'
        />
      </InstantSearch>

      {selectedConditions.filter(Boolean).map((condition) => (
        <input
          key={condition}
          name='comorbidities'
          type='hidden'
          value={condition}
        />
      ))}

      {comorbiditiesToDisplay
        .filter((comorbidity) => !selectedConditions.includes(comorbidity.name))
        .map((unselectedComorbidity) => (
          <input
            key={unselectedComorbidity.name}
            name='unselectedComorbidities'
            type='hidden'
            value={unselectedComorbidity.name}
          />
        ))}
    </div>
  )
}
